import React, { useEffect, useState, useCallback } from "react";
import { analysisAPI } from "../../utils/analysisAPI";
import MetricCard from "../../components/MetricCard";

function Predictions() {
  const [models, setModels] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedMeta, setSelectedMeta] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [prediction, setPrediction] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        const payload = await analysisAPI.listAnalyses(null, { status: "COMPLETED" });
        const list = Array.isArray(payload?.list) ? payload.list : payload?.list ?? payload;
        if (!mounted) return;
        setModels(list || []);
        setError(null);
      } catch (e) {
        if (!mounted) return;
        setError(e.message || "Failed to load models for predictions.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  const loadHistory = useCallback(async (id) => {
    if (!id) return;
    try {
      setHistoryLoading(true);
      const data = await analysisAPI.predictionHistory(id);
      setHistory(data?.results || []);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const handleSelectModel = async (id) => {
    setSelectedId(id);
    setSelectedMeta(null);
    setFormValues({});
    setPrediction(null);
    setHistory([]);
    if (!id) return;
    try {
      const analysis = await analysisAPI.getAnalysis(id);
      const features =
        analysis?.configuration?.features ||
        analysis?.results?.features ||
        [];
      setSelectedMeta({
        id: analysis.id,
        model_name: analysis.model_name,
        model_category: analysis.model_category,
        features,
      });
      const initial = {};
      features.forEach((f) => { initial[f] = ""; });
      setFormValues(initial);
      loadHistory(id);
    } catch (e) {
      setError(e.message || "Failed to load model details.");
    }
  };

  const handleChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedMeta) return;
    setSubmitting(true);
    setPrediction(null);
    setError(null);
    try {
      const result = await analysisAPI.predict(selectedMeta.id, formValues);
      setPrediction(result);
      loadHistory(selectedMeta.id);
    } catch (e) {
      setError(e.message || "Prediction failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="analysis-dashboard">
      <div className="dashboard-header">
        <div className="header-content">
          <div>
            <h1>Predictions</h1>
            <p className="subtitle">
              Choose a completed analysis, fill in feature values, and run a scenario prediction.
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading completed models...</p>
        </div>
      ) : models.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">—</div>
          <h3>No completed models yet</h3>
          <p>Run an analysis and come back here to generate predictions.</p>
        </div>
      ) : (
        <>
          <div className="stats-section">
            <MetricCard label="Completed Models" value={models.length} tone="primary" />
          </div>

          {/* Model selector */}
          <div className="filters-section">
            <h3>Choose Model</h3>
            <div className="filters-grid">
              <div className="filter-group">
                <label>Completed Analyses</label>
                <select
                  className="filter-select"
                  value={selectedId || ""}
                  onChange={(e) => handleSelectModel(Number(e.target.value) || null)}
                >
                  <option value="">Select a model...</option>
                  {models.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.model_name} ({m.model_category})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Input form */}
          {selectedMeta && (
            <div className="result-section">
              <h3>Input Values</h3>
              <form onSubmit={handleSubmit}>
                <div className="filters-grid">
                  {selectedMeta.features.map((f) => (
                    <div key={f} className="filter-group">
                      <label>{f}</label>
                      <input
                        type="text"
                        className="filter-input"
                        value={formValues[f] ?? ""}
                        onChange={(e) => handleChange(f, e.target.value)}
                        required
                      />
                    </div>
                  ))}
                </div>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitting}
                  style={{ marginTop: "16px" }}
                >
                  {submitting ? "Predicting..." : "Run Prediction"}
                </button>
              </form>
            </div>
          )}

          {/* Latest prediction result */}
          {prediction && (
            <div className="result-section results-section">
              <h3>Prediction Result</h3>
              <div className="results-grid">
                <div className="result-item">
                  <div className="result-label">
                    {prediction.model_category === "ANOMALY_DETECTION"
                      ? "Anomaly Status"
                      : "Predicted Value"}
                  </div>
                  <div className="result-value">
                    {prediction.model_category === "ANOMALY_DETECTION"
                      ? prediction.anomaly_flag
                        ? "⚠ Anomaly Detected"
                        : "✓ Normal"
                      : String(prediction.prediction)}
                  </div>
                </div>
                <div className="result-item">
                  <div className="result-label">Model</div>
                  <div className="result-value">
                    {prediction.model_name} ({prediction.model_category})
                  </div>
                </div>
                {prediction.model_category === "CLASSIFICATION" &&
                  Array.isArray(prediction.probabilities) && (
                    <div className="result-item">
                      <div className="result-label">Class Probabilities</div>
                      <div className="result-value">
                        {prediction.probabilities
                          .map((p, idx) => `Class ${idx}: ${(p * 100).toFixed(1)}%`)
                          .join(" | ")}
                      </div>
                    </div>
                  )}
                {prediction.model_category === "ANOMALY_DETECTION" &&
                  typeof prediction.anomaly_score === "number" && (
                    <div className="result-item">
                      <div className="result-label">Anomaly Score</div>
                      <div className="result-value">
                        {prediction.anomaly_score.toFixed(4)}
                      </div>
                    </div>
                  )}
              </div>
            </div>
          )}

          {/* Prediction history table */}
          {selectedMeta && (
            <div className="result-section">
              <h3>Prediction History</h3>
              {historyLoading ? (
                <div className="loading-container" style={{ padding: "24px" }}>
                  <div className="spinner"></div>
                </div>
              ) : history.length === 0 ? (
                <p style={{ color: "var(--text-grey)", fontSize: "14px" }}>
                  No predictions recorded yet for this model.
                </p>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                    <thead>
                      <tr>
                        <th style={thStyle}>Time</th>
                        <th style={thStyle}>Inputs</th>
                        <th style={thStyle}>Result</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((log) => {
                        const out = log.output || {};
                        return (
                          <tr key={log.id} style={{ borderBottom: "1px solid var(--border-grey, #eee)" }}>
                            <td style={tdStyle}>
                              {new Date(log.created_at).toLocaleString()}
                            </td>
                            <td style={tdStyle}>
                              {Object.entries(log.input_values || {})
                                .map(([k, v]) => `${k}: ${v}`)
                                .join(", ")}
                            </td>
                            <td style={tdStyle}>
                              {out.model_category === "ANOMALY_DETECTION"
                                ? out.anomaly_flag
                                  ? "⚠ Anomaly"
                                  : "✓ Normal"
                                : String(out.prediction ?? "—")}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const thStyle = {
  textAlign: "left",
  padding: "8px 12px",
  background: "var(--bg-grey, #f5f5f5)",
  fontWeight: 600,
  borderBottom: "2px solid var(--border-grey, #e0e0e0)",
};

const tdStyle = {
  padding: "8px 12px",
  verticalAlign: "top",
  maxWidth: "280px",
  wordBreak: "break-word",
};

export default Predictions;
